import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { displayNotify } from "../services/Notifications/NotificationServices";
import { useNavigate } from "react-router";

interface Subtype {
    global_type_name: string,
    sub_type_name: string
}

interface Host {
    slug: string,
    is_main_host: boolean
}

interface eventInfo {
    name: string,
    description: string,
    subtypes?: Subtype[],
    reg_type: string,
    is_reg_validation_required: boolean,
    tags?: string[],
    ground?: string,
    start_datetime: string,
    end_datetime: string,
    pricing?: string,
    event_hosts?: Host[],
    is_entry_reason_required: boolean,
    rent_available: boolean,
    state: string,
    is_active: boolean,
    is_visible: boolean,
    is_payment_accepted?: boolean,
    imagesFile?: File[],
    previewFile?: File,
    sides?: any[]
    kmzFile?: File
}

const uploadEventImage = async (file: File, event_slug: string) => {
    await axios.put(`/events/${event_slug}/image/`, file, {
        headers: {
            'content-type': file.type,
        }
    })
}

const uploadEventSides = async (side: any, event_slug: string) => {
    await axios.post(`/events/${event_slug}/parties/`, side)
}

const uploadEventPreview = async (file: File, event_slug: string) => {
    await axios.put(`/events/${event_slug}/image/true/`, file, {
        headers: { 'Content-Type': file.type },
    })
}

const uploadEventKmz = async (file: File, event_slug: string) => {
    await axios.put(`/events/${event_slug}/map/`, file, {
        headers: { 'Content-Type': file.type },
    })
}

export default function useCreateEvent() {
    const navigate = useNavigate()

    return useMutation({
        mutationFn: async (newEvent: eventInfo) => {
            const response = await axios.post('/events/', newEvent)
            const slug = response.data.slug

            const uploadErrors = []

            if (slug) {

                if (newEvent.imagesFile?.length) {
                    await Promise.all(
                        newEvent.imagesFile.map(async (file) => {
                            try {
                                uploadEventImage(file, slug)
                            } catch {
                                uploadErrors.push('Ошибка загрузки изображений')
                            }
                        }))
                }

                if (newEvent.previewFile) {
                    try {
                        await uploadEventPreview(newEvent.previewFile, slug)
                    } catch {
                        uploadErrors.push('Ошибка загрузки обложки')
                    }
                }

                if (newEvent.kmzFile) {
                    try {
                        await uploadEventKmz(newEvent.kmzFile, slug)
                    } catch {
                        uploadErrors.push('Ошибка загрузки файла карты')
                    }
                }

                if (newEvent.sides?.length) {
                    await Promise.all(
                        newEvent.sides.map((side) => {
                            try {
                                uploadEventSides(side, slug)
                            } catch {
                                uploadErrors.push('Ошибка загрузки сторон')
                            }
                        }))
                }
            }

            return { slug, uploadErrors }

        },

        onSuccess: ({ slug, uploadErrors }) => {

            displayNotify({
                status: 'success',
                title: 'Успех!',
                description: 'Мероприятие успешно создано'
            })

            if (slug) {
                navigate(`/gameinfo/${slug}`)
            }

            if (uploadErrors.length) {
                uploadErrors.forEach((e) =>
                    displayNotify({
                        status: 'error',
                        title: e,
                        description: 'Повторите попытку позже в режиме редактирования мероприятия'
                    }))
            }
        },

        onError: (error) => {
            if (axios.isAxiosError(error) && error.response?.status === 400) {
                const data = error.response.data;
                const hostError = Array.isArray(data?.event_hosts) ? data.event_hosts[0] : null;

                if (hostError && hostError.includes('Пользователь не состоит в орг. группах:')) {
                    displayNotify({
                        status: 'error',
                        title: 'Мероприятие не создано',
                        description: 'Вы не состоите в выбранных орг. группах'
                    })
                    return
                }
            }

            displayNotify({
                status: 'error',
                title: 'Мероприятие не создано',
                description: 'Проверьте корректность введённых данных или повторите попытку позже'
            })
        }
    })
}